import { createFileRoute, Link } from "@tanstack/react-router";

const CANONICAL = "https://www.casamipajobe.com/trust";

export const Route = createFileRoute("/trust")({
  head: () => ({
    meta: [
      { title: "Trust, Security & Privacy — Casa Mi Pajobe" },
      { name: "robots", content: "index,follow" },
      {
        name: "description",
        content:
          "How Casa Mi Pajobe handles your booking data, account security, payments and privacy. Maintained by the villa owners.",
      },
      { property: "og:title", content: "Trust, Security & Privacy — Casa Mi Pajobe" },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: TrustPage,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="text-xl font-semibold mb-3">{title}</h2>
      <div className="space-y-3 text-muted-foreground leading-relaxed">{children}</div>
    </section>
  );
}

function TrustPage() {
  return (
    <main className="mx-auto max-w-3xl px-4 py-12 sm:py-16">
      <header className="mb-10">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">
          Trust, Security &amp; Privacy
        </h1>
        <p className="mt-3 text-muted-foreground">
          This page is maintained by the owners of Casa Mi Pajobe to answer common
          security and privacy questions about our booking website. It describes our
          current practices and is not an independent certification.
        </p>
      </header>

      <Section title="Account access &amp; authentication">
        <p>
          Guest inquiries can be submitted without an account. The admin area used by
          the villa owners requires email-based sign-in, and sensitive actions are
          restricted to accounts with an explicit admin role granted server-side.
        </p>
      </Section>

      <Section title="Data we collect">
        <p>
          When you submit a booking inquiry we store the details you provide (name,
          contact details, dates, party size and any message). We also keep basic,
          aggregated visit statistics (page, referrer, country) to understand how the
          site is used. We do not sell personal data.
        </p>
      </Section>

      <Section title="Payments">
        <p>
          Payments are processed by Stripe. Card details are entered on Stripe&apos;s
          hosted surfaces and are never stored on our servers; we only receive the
          payment status and a reference for your booking.
        </p>
      </Section>

      <Section title="Hosting &amp; infrastructure">
        <p>
          The website runs on the Lovable platform with a managed Postgres database.
          Connections are encrypted in transit (HTTPS/TLS). Access to the database is
          protected by row-level security policies so each record is only readable by
          the accounts that need it.
        </p>
      </Section>

      <Section title="Retention &amp; deletion">
        <p>
          Booking records are kept for as long as needed to administer your stay and
          to meet legal/accounting obligations. To request a copy of your data or its
          deletion, contact us using the address below.
        </p>
      </Section>

      <Section title="Cookies &amp; analytics">
        <p>
          We use a small number of first-party cookies and storage entries that are
          required for the site to work (for example to remember your language and
          session). Aggregated visit counts are stored without third-party ad
          trackers.
        </p>
      </Section>

      <Section title="Reporting a security issue">
        <p>
          If you believe you have found a security issue, please email{" "}
          <a className="underline" href="mailto:info@casamipajobe.com">
            info@casamipajobe.com
          </a>{" "}
          with a description and steps to reproduce. We will respond as soon as
          possible.
        </p>
      </Section>

      <p className="text-sm text-muted-foreground">
        <Link to="/" className="underline">
          Back to home
        </Link>
      </p>
    </main>
  );
}